/**
 * WHAT THE 3D SELECTION MEANS FOR THE ASSET TABLE (audit 2026-10-05, bug 6; the rule agreed with
 * Idan the same day).
 *
 *   - one or more ASSETS selected, in 3D or from the table, on any site: the table shrinks to them;
 *   - only elements that are NOT assets: the list stays as it is, and a line above it names each
 *     one — "Floor [481276]: not an asset yet" — with Create asset for people who may create assets;
 *   - both: the table shrinks to the assets, and the line lists the rest.
 *
 * Before, a click on something that was not an asset was passed on as if it were one, so the table
 * switched to the selection, found nothing and said "No assets match these choices"; and on a site
 * with several models the table never heard about 3D clicks at all (Autodesk sends only its
 * aggregate selection event there — the event this reads).
 *
 * Which element is an asset is decided exactly as the Element panel decides it
 * (elementAsset.findAssetForElement): the line offers Create asset precisely where the panel would,
 * and never for an element whose number only matches another model's asset.
 */

import { findAssetForElement } from './elementAsset';
import { getPOIElementDbId, getPOISourceFile } from '../../utils/POIFieldExtractor';
import type { ViewerSelectedElement } from '../../types/poi-table';

/** The selected elements of one model — one entry per model in Autodesk's aggregate selection. */
export interface SceneSelectionGroup {
  model?: any;
  dbIds: number[];
}

export interface ClassifySceneSelectionOptions {
  /** The file assets record for this model, or null when unknown. */
  sourceFileOf: (model: any) => string | null;
  /** More than one model loaded: element numbers repeat across them, so the file decides. */
  multiModel: boolean;
  /** The element's name as the model gives it, e.g. "Floor [481276]". */
  nameOf?: (model: any, dbId: number) => string | null;
  /**
   * An asset whose element stands exactly where this one does, for an element that has none of
   * its own — a coincident element that is not the node the asset was recorded from.
   */
  coincidentAsset?: (model: any, dbId: number) => any | null;
}

const validDbId = (dbId: unknown): dbId is number =>
  typeof dbId === 'number' && Number.isFinite(dbId) && dbId > 0;

/** Autodesk's AGGREGATE_SELECTION_CHANGED_EVENT `selections` → one group per model. */
export function groupsFromAggregateEvent(selections: unknown): SceneSelectionGroup[] {
  if (!Array.isArray(selections)) return [];
  return selections
    .map((entry: any) => ({ model: entry?.model, dbIds: (entry?.dbIdArray || entry?.selection || []).filter(validDbId) }))
    .filter((group) => group.dbIds.length > 0);
}

/** The viewer's current selection, every model (getSelection() is the main model's only). */
export function readAggregateSelection(viewer: any): SceneSelectionGroup[] {
  try {
    const aggregate = viewer?.getAggregateSelection?.();
    if (Array.isArray(aggregate)) return groupsFromAggregateEvent(aggregate);
    const ids = viewer?.getSelection?.();
    return Array.isArray(ids) && ids.some(validDbId) ? [{ model: viewer?.model, dbIds: ids.filter(validDbId) }] : [];
  } catch {
    return [];
  }
}

const fileKey = (sourceFile?: string | null) => (sourceFile || '').toLowerCase();

/**
 * The 3D selection as the asset table receives it: each selected element is either one of its
 * assets — in the shape the table has always matched (a bare number on a one-model site, number
 * and file on a federated one) — or `notAsset`, with the name the line shows. Each asset and each
 * element appears once, in selection order.
 */
export function classifySceneSelection(
  groups: SceneSelectionGroup[],
  pois: any[],
  options: ClassifySceneSelectionOptions
): ViewerSelectedElement[] {
  const out: ViewerSelectedElement[] = [];
  const seen = new Set<string>();
  for (const group of groups) {
    const sourceFile = options.sourceFileOf(group.model);
    for (const dbId of group.dbIds) {
      if (!validDbId(dbId)) continue;
      const asset = findAssetForElement(pois, dbId, sourceFile, options.multiModel)
        || options.coincidentAsset?.(group.model, dbId)
        || null;
      if (asset) {
        const assetDbId = getPOIElementDbId(asset) ?? dbId;
        const assetFile = getPOISourceFile(asset);
        const key = `asset:${fileKey(assetFile)}:${assetDbId}`;
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(options.multiModel ? { dbId: assetDbId, ...(assetFile ? { sourceFile: assetFile } : {}) } : assetDbId);
        continue;
      }
      const key = `element:${fileKey(sourceFile)}:${dbId}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({
        dbId,
        ...(sourceFile ? { sourceFile } : {}),
        notAsset: true,
        label: options.nameOf?.(group.model, dbId) || null,
      });
    }
  }
  return out;
}

/** True for a selected element that is not an asset (it goes on the table's "not an asset yet" line). */
export const isNotAssetSelection = (item: ViewerSelectedElement): boolean =>
  typeof item === 'object' && item !== null && item.notAsset === true;

/** Same items, same order — so passing the selection on again can be skipped when nothing changed. */
export function sameSceneSelection(a: ViewerSelectedElement[], b: ViewerSelectedElement[]): boolean {
  if (a.length !== b.length) return false;
  const norm = (item: ViewerSelectedElement) => (typeof item === 'number'
    ? `n:${item}`
    : `o:${item.dbId}:${fileKey(item.sourceFile)}:${item.notAsset ? 1 : 0}:${item.label || ''}`);
  return a.every((item, index) => norm(item) === norm(b[index]));
}
